import React from "react";
import PropTypes from "prop-types";
import DataTable from "../../../../components/DataTable";
import { getStatusMeta, STATUS_TONE_CLASSES } from "../../../../constants/order-status";
import { formatDuration } from "../../../../utils/format";
import TatCell from "./TatCell";
import RowActions from "../../../../components/RowActions";
import EmptyValue from "../../../../components/EmptyValue";
import { sourceLabel } from "../../../constants/tatFilters";

// TatTable — the interval columns are DERIVED from `intervals`
// (i.e. the API's `meta.intervals`), never from a hardcoded list. When the
// backend starts instrumenting a new stage it just appears in that array and
// the column shows up here with no frontend deploy. The only fixed columns
// are the order-identity ones on the left.

/**
 * "Is this row's blankness meaningful or expected" — one glyph, not a filter.
 *
 * Both checks read `row.dataQuality` (useTatOrders.js's toRow passes it through
 * raw from the API's `dataQuality` object). Deliberately not a third heavier
 * "Data quality" chip row — that UI was dropped earlier as a v2 addition the
 * prototype doesn't have; this is a single dot with a tooltip, in keeping with
 * this page's own principle of stating plainly what's known without adding
 * chrome the prototype never asked for.
 *
 * @returns {string|null} the tooltip text, or null to render nothing.
 */
export function dataQualityNote(row) {
    const dq = row.dataQuality;
    if (!dq) return null;
    // Same concept TatCell's SWEEP state flags per-interval, at the whole-order
    // level: this order's completion was closed by a bulk administrative action,
    // not an observed event.
    if (dq.closedBySweep) return "Closed by an administrative sweep, not an observed delivery.";
    // No linked quotation AND no linked lead at all — every early-funnel NO_DATA
    // cell on this row is structural (there's nothing to link to), not a gap
    // worth chasing.
    if (!dq.hasQuotation && !dq.hasLead) return "No linked quotation or lead for this order.";
    return null;
}

/**
 * The order id, which is also this row's handle onto its own timeline.
 *
 * This used to be deliberately dead text: legacy's ID column (orderTable/table-layout.jsx)
 * in elm green, minus its `underline cursor-pointer`, because "TAT's id opens nothing, and
 * claiming link affordances for dead text is the one legacy detail worth not copying."
 * That reasoning stands; what changed is the premise. With `onViewTimeline` wired up the id
 * DOES open something, so the legacy affordance is reinstated — and only then: with no
 * handler passed this still renders as plain text rather than a button that looks
 * clickable and isn't.
 *
 * The id is the right handle for it. It is the one cell on the row that names the whole
 * order rather than one measurement of it, and the timeline is the whole order's story.
 * Edit keeps its own icon button in the Action column; these do not compete.
 */
function OrderIdCell({ row, onViewTimeline }) {
    const note = dataQualityNote(row);
    const base = "inline-flex items-center gap-1.5 whitespace-nowrap font-sans font-medium text-elm-500";
    const content = (
        <>
            {row.id}
            {note && (
                <span
                    role="img"
                    aria-label="Data quality note"
                    title={note}
                    className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500"
                />
            )}
        </>
    );

    if (!onViewTimeline) return <span className={base}>{content}</span>;

    return (
        <button
            type="button"
            // DataTable rows can carry their own onRowClick; keep this from firing it too.
            onClick={(event) => {
                event.stopPropagation();
                onViewTimeline(row);
            }}
            // The visible text is just the bare id, which is no use read aloud on its
            // own; `aria-label` gives the control a name that says what it does, and
            // `title` shows sighted users the same thing on hover.
            aria-label={`View status timeline for order ${row.id}`}
            title={`View status timeline for order ${row.id}`}
            className={`${base} underline cursor-pointer`}
        >
            {content}
        </button>
    );
}

OrderIdCell.propTypes = {
    row: PropTypes.object.isRequired,
    onViewTimeline: PropTypes.func,
};

OrderIdCell.defaultProps = { onViewTimeline: null };

/**
 * The identity columns that precede every interval column.
 *
 * A builder rather than a constant because the id column needs the page's timeline
 * handler. The export calls it with no handler — a spreadsheet has no click targets, and
 * the labels and values are identical either way.
 */
const buildIdentityColumns = (onViewTimeline) => [
    {
        key: "id",
        label: "Order",
        exportValue: (row) => row.id,
        render: (row) => <OrderIdCell row={row} onViewTimeline={onViewTimeline} />,
    },
    {
        key: "patientName",
        label: "Patient",
        exportValue: (row) => row.patientName || "",
        render: (row) => <span className="whitespace-nowrap font-sans font-bold text-black">{row.patientName}</span>,
    },
    {
        key: "source",
        label: "Source",
        exportValue: (row) => sourceLabel(row.source) || "",
        // Sits immediately after Patient: who the medicine is for and where the order
        // came from are the pair an operator reads as one fact.
        //
        // Unconditional, unlike the partner-name column it replaced. That one was hidden
        // under the DIRECT scope because `partnerName` is null on every direct order, so
        // the column could only ever be dashes. `source` is populated across both scopes
        // — META, APP and INTERNAL are direct sources — so there is no scope in which
        // this column is dead weight, and one that appeared and vanished would just be
        // an unexplained layout shift.
        render: (row) => {
            const label = sourceLabel(row.source);
            return label ? (
                <span className="whitespace-nowrap font-sans">{label}</span>
            ) : (
                <EmptyValue />
            );
        },
    },
    {
        key: "source",
        label: "Source",
        exportValue: (row) => sourceLabel(row.source) || "",
        // Sits immediately after Patient: who the medicine is for and where the order
        // came from are the pair an operator reads as one fact.
        //
        // Unconditional, unlike the partner-name column it replaced. That one was hidden
        // under the DIRECT scope because `partnerName` is null on every direct order, so
        // the column could only ever be dashes. `source` is populated across both scopes
        // — META, APP and INTERNAL are direct sources — so there is no scope in which
        // this column is dead weight, and one that appeared and vanished would just be
        // an unexplained layout shift.
        render: (row) => {
            const label = sourceLabel(row.source);
            return label ? (
                <span className="whitespace-nowrap font-sans">{label}</span>
            ) : (
                <EmptyValue />
            );
        },
    },
    {
        key: "address",
        label: "Address",
        // Addresses run long; unlike the other identity columns this one is
        // allowed to wrap rather than forcing the row's width out to fit one
        // line (DataTable.jsx's `wrap` option).
        wrap: true,
        exportValue: (row) => row.address || "",
        render: (row) => <span className="font-sans">{row.address}</span>,
    },
    {
        key: "status",
        label: "Status",
        exportValue: (row) => getStatusMeta(row.status).label,
        render: (row) => {
            const { label, tone } = getStatusMeta(row.status);
            const toneClasses = STATUS_TONE_CLASSES[tone];
            // Legacy filter-chip geometry (10px / px-2 py-1 / rounded-[10px] /
            // gap-2), borderless like every legacy chip — STATUS_TONE_CLASSES
            // no longer carries a `border` key. TAT's status is read-only, so
            // this is a chip and not legacy Orders' live <select>.
            return (
                <span
                    className={`inline-flex items-center gap-2 whitespace-nowrap rounded-[10px] px-2 py-1 text-[10px] font-semibold font-sans ${toneClasses.bg} ${toneClasses.text}`}
                >
                    {label}
                </span>
            );
        },
    },
];

/**
 * How an interval cell leaves the page in a spreadsheet.
 *
 * This is NOT just the duration. TatCell's whole purpose is that a number you can
 * trust and a number you cannot are told apart on screen; an export that printed
 * "2d 4h" for a COMPLETE cell and for a STALE one would throw that away, and a
 * stalled order would be averaged in as if it had finished. So anything other than
 * COMPLETE carries its state alongside the figure, and a cell with no figure at all
 * exports the state on its own.
 */
export function exportInterval(cell) {
    if (!cell) return "";
    const measured = cell.minutes !== null && cell.minutes !== undefined;
    if (cell.state === "COMPLETE") return measured ? formatDuration(cell.minutes) : "";
    return measured ? `${cell.state} · ${formatDuration(cell.minutes)}` : cell.state;
}

/**
 * The spreadsheet's columns, derived from the SAME `intervals` the table renders.
 *
 * Lives here rather than in the page so the two can't drift: a stage the backend starts
 * instrumenting shows up in the export for the same reason it shows up as a column —
 * because it appeared in `meta.intervals` — with no code change in either place. The
 * selection checkbox is not included; it is chrome, not data.
 */
export function buildExportColumns(intervals) {
    return [
        ...buildIdentityColumns(null).map((column) => ({
            label: column.label,
            value: column.exportValue,
        })),
        ...intervals.map((interval) => ({
            label: interval.label,
            value: (row) => exportInterval(row.intervals?.[interval.key]),
        })),
    ];
}

function TatTable({
    intervals,
    rows,
    selectedIds,
    onToggleRow,
    onToggleAll,
    onEditOrder,
    onDeleteOrder,
    onCheckDeletable,
    onViewTimeline,
}) {
    const selectable = Boolean(onToggleRow && onToggleAll);
    const selected = selectedIds || new Set();
    const allSelected = rows.length > 0 && rows.every((row) => selected.has(row.id));

    const selectColumn = {
        key: "select",
        // Chrome, not data — TatTable's consumer excludes these from the export.
        excludeFromExport: true,
        label: (
            <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleAll}
                aria-label="Select all rows"
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
        ),
        render: (row) => (
            <input
                type="checkbox"
                checked={selected.has(row.id)}
                onChange={() => onToggleRow(row.id)}
                onClick={(event) => event.stopPropagation()}
                aria-label={`Select order ${row.id}`}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
        ),
    };

    // Legacy opens every table with a row counter. It numbers within the page, as
    // legacy does — not page-aware — so the column reads identically on both.
    const indexColumn = {
        key: "rowIndex",
        label: "#",
        excludeFromExport: true,
        render: (row, rowIndex) => <span className="font-sans">{rowIndex + 1}</span>,
    };

    // Legacy hides delete when an order has a SUCCESS or PENDING payment
    // (quotationsTable/table-instance.jsx:30). A TAT row comes from /ops/tat/orders,
    // whose shape carries no `payments`, so that rule cannot be answered from the row.
    // Rather than drop the guard or pay for a payments lookup on every visible row, the
    // page supplies `onCheckDeletable`, which RowActions runs once — when the user
    // actually clicks delete, before the confirm.
    const actionColumn = {
        key: "actions",
        label: "Action",
        excludeFromExport: true,
        render: (row) => (
            <RowActions
                onEdit={() => onEditOrder(row)}
                canDelete={Boolean(onDeleteOrder)}
                onBeforeDelete={onCheckDeletable ? () => onCheckDeletable(row) : null}
                onDelete={() => onDeleteOrder(row)}
            />
        ),
    };

    const columns = [
        ...(selectable ? [selectColumn] : []),
        indexColumn,
        ...buildIdentityColumns(onViewTimeline),
        ...intervals.map((interval) => ({
            key: interval.key,
            label: interval.label,
            render: (row) => <TatCell value={row.intervals?.[interval.key]} />,
            exportValue: (row) => exportInterval(row.intervals?.[interval.key]),
        })),
        ...(onEditOrder ? [actionColumn] : []),
    ];

    return <DataTable columns={columns} rows={rows} />;
}

TatTable.propTypes = {
    /** Omit to render no Action column. */
    onEditOrder: PropTypes.func,
    /** Omit to render no delete button. */
    onDeleteOrder: PropTypes.func,
    /** Async guard: resolve a reason string to block the delete, or null to allow. */
    onCheckDeletable: PropTypes.func,
    /**
     * Called with the row when its order id is clicked. Omit and the id renders as
     * plain text — the id is only given a link affordance when it actually opens one.
     */
    onViewTimeline: PropTypes.func,
    /** Selection is optional; omit the handlers and no checkbox column renders. */
    selectedIds: PropTypes.instanceOf(Set),
    onToggleRow: PropTypes.func,
    onToggleAll: PropTypes.func,
    /** The API's `meta.intervals`: ordered `{key, label}` stage descriptors. */
    intervals: PropTypes.arrayOf(
        PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.node.isRequired })
    ),
    /** Order rows; each carries an `intervals` object keyed by interval key. */
    rows: PropTypes.arrayOf(PropTypes.object),
};

// One assignment, not two: this file previously set `defaultProps` twice and the
// second call replaced the first outright, silently dropping every default it
// declared. Keep all of them here.
TatTable.defaultProps = {
    onEditOrder: null,
    onDeleteOrder: null,
    onCheckDeletable: null,
    onViewTimeline: null,
    selectedIds: null,
    onToggleRow: null,
    onToggleAll: null,
    intervals: [],
    rows: [],
};

export default TatTable;
